import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import { Boxes, ClipboardList, ScanLine } from 'lucide-react';
import {
  createBrowserRouter,
  Link,
  NavLink,
  Outlet,
  ScrollRestoration,
  useRouteError,
} from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { ApiError, api, queryKeys } from './api/client.ts';
import { AccessGate } from './components/access-gate.tsx';
import { ErrorNotice, useOnline } from './components/ui.tsx';
import { I18nProvider, useI18n } from './i18n/index.tsx';
import { ContainerScreen } from './routes/container.tsx';
import { FleetScreen } from './routes/fleet.tsx';
import { HandoverScreen } from './routes/handover.tsx';
import { HomeScreen } from './routes/home.tsx';
import { IdentifyScreen } from './routes/identify.tsx';
import { ReportScreen } from './routes/report.tsx';
import { TryScreen } from './routes/try.tsx';
import { WalkaroundScreen } from './routes/walkaround.tsx';

/** A 401 means the session ended: ask the server again, which brings back the access-code form. */
function onError(error: unknown) {
  if (error instanceof ApiError && error.status === 401) {
    void queryClient.invalidateQueries({ queryKey: queryKeys.session });
  }
}

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError }),
  mutationCache: new MutationCache({ onError }),
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      retry: (failures, error) =>
        !(error instanceof ApiError && error.status < 500) && failures < 1,
    },
    mutations: { retry: 0 },
  },
});

function Shell() {
  const { t, language, setLanguage } = useI18n();
  const online = useOnline();
  return (
    <>
      <header className="app-header no-print">
        <Link to="/" className="brand">
          <img src="/icons/favicon.svg" alt="" width={30} height={30} />
          Plomba
        </Link>
        <nav className="app-nav" aria-label={t('nav.label')}>
          <NavLink to="/" end>
            <ClipboardList size={18} aria-hidden /> {t('nav.handovers')}
          </NavLink>
          <NavLink to="/containers">
            <Boxes size={18} aria-hidden /> {t('nav.fleet')}
          </NavLink>
        </nav>
        <div className="app-header-end">
          <Link to="/identify" className="button accent small inspect-cta">
            <ScanLine size={18} aria-hidden /> {t('nav.inspect')}
          </Link>
          <button
            type="button"
            className="lang-toggle"
            aria-label={t('app.language')}
            onClick={() => setLanguage(language === 'en' ? 'pl' : 'en')}
          >
            {language === 'en' ? 'PL' : 'EN'}
          </button>
        </div>
      </header>
      {!online && <p className="offline-banner no-print">{t('common.offline')}</p>}
      <main>
        <ScrollRestoration />
        <AccessGate>
          <Outlet />
        </AccessGate>
      </main>
      <Footer />
      <nav className="tabbar no-print" aria-label={t('nav.label')}>
        <NavLink to="/" end>
          <ClipboardList size={22} aria-hidden />
          {t('nav.handovers')}
        </NavLink>
        <NavLink to="/identify" className="inspect">
          <ScanLine size={28} aria-hidden />
          <span className="visually-hidden">{t('nav.inspect')}</span>
        </NavLink>
        <NavLink to="/containers">
          <Boxes size={22} aria-hidden />
          {t('nav.fleet')}
        </NavLink>
      </nav>
    </>
  );
}

/** The operator's notice, if any, and where the source code is. */
function Footer() {
  const { t } = useI18n();
  const instance = useQuery({
    queryKey: queryKeys.instance,
    queryFn: api.instance,
    staleTime: Number.POSITIVE_INFINITY,
  });
  return (
    <footer className="app-footer no-print">
      {instance.data?.notice && <p>{instance.data.notice}</p>}
      <p>
        {t('app.openSource')}{' '}
        <a href="https://github.com/Lisovate/plomba" target="_blank" rel="noreferrer">
          github.com/Lisovate/plomba
        </a>
      </p>
    </footer>
  );
}

function RouteError() {
  return <ErrorNotice error={useRouteError()} />;
}

const router = createBrowserRouter([
  {
    element: <Shell />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <HomeScreen /> },
      { path: 'identify', element: <IdentifyScreen /> },
      { path: 'try', element: <TryScreen /> },
      { path: 'containers', element: <FleetScreen /> },
      { path: 'containers/:number', element: <ContainerScreen /> },
      { path: 'handovers/:id', element: <HandoverScreen /> },
      { path: 'handovers/:id/report', element: <ReportScreen /> },
      { path: 'handovers/:id/walkaround', element: <WalkaroundScreen /> },
    ],
  },
]);

export function App() {
  return (
    <I18nProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </I18nProvider>
  );
}
